import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Apple, Google } from '@mui/icons-material';
import { useWallet } from '@aptos-labs/wallet-adapter-react';
import ConnectButton from './ConnectButton';
import TermsAndConditionModal from './TermsAndConditionModal';
import { canonicalWalletName, walletFingerprint } from 'utils/wallets';
import useLogout from 'hooks/useLogout';
import { useUserContext } from 'context/userContext';

interface LoginModalProps {
	open: boolean;
	onClose: () => void;
}

export default function LoginModal({ open, onClose }: LoginModalProps) {
	const theme = useTheme();
	const [connectingWallet, setConnectingWallet] = useState<string | null>(
		null,
	);
	const [termsModalOpen, setTermsModalOpen] = useState(false);

	const walletApi = useWallet();
	const { wallets = [], notDetectedWallets = [] } = walletApi;
	const { mutate: logout } = useLogout();
	const { isLoggedIn } = useUserContext();

	const TERMS_ACCEPTED_KEY = 'termsAndConditionAccepted';
	const hasAcceptedTerms = () => {
		if (typeof window === 'undefined') {
			return false;
		}
		return window.localStorage.getItem(TERMS_ACCEPTED_KEY) === 'true';
	};

	const handleSuccess = () => {
		setConnectingWallet(null);
		if (hasAcceptedTerms()) {
			onClose();
			return;
		}
		setTermsModalOpen(true);
		onClose();
	};

	const handleTermsCancel = () => {
		logout();
		walletApi.disconnect();
		setTermsModalOpen(false);
	};

	const handleTermsAccept = () => {
		if (typeof window !== 'undefined') {
			window.localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
		}
		setTermsModalOpen(false);
	};

	useEffect(() => {
		if (isLoggedIn && !hasAcceptedTerms() && !termsModalOpen) {
			setTermsModalOpen(true);
		}
	}, [isLoggedIn, termsModalOpen]);

	const allWallets = useMemo(
		() => [...wallets, ...(notDetectedWallets ?? [])],
		[wallets, notDetectedWallets],
	);

	const orderedWallets = useMemo(() => {
		const priority: Record<string, number> = {
			Petra: 0,
			'Continue with Google': 1,
			Google: 1,
			Apple: 2,
		};

		return [...allWallets].sort((a, b) => {
			const pa = priority[canonicalWalletName(a.name)] ?? 99;
			const pb = priority[canonicalWalletName(b.name)] ?? 99;
			return pa - pb;
		});
	}, [allWallets]);

	const walletOptions = useMemo(() => {
		const seen = new Set<string>();
		const allowList = new Set(['petra', 'continue with google', 'apple']);

		return orderedWallets.reduce<
			Array<{ adapterName: string; displayName: string; icon?: string }>
		>((acc, wallet) => {
			const canonical = canonicalWalletName(wallet.name);
			const normalized = (canonical || wallet.name).toLowerCase();
			const key = walletFingerprint(canonical || wallet.name);
			if (!key || seen.has(key) || !allowList.has(normalized)) {
				return acc;
			}
			seen.add(key);

			acc.push({
				adapterName: wallet.name,
				displayName: canonical || wallet.name,
				icon: wallet.icon as string | undefined,
			});

			return acc;
		}, []);
	}, [orderedWallets]);

	const renderStartIcon = (walletName: string, walletIcon?: string) => {
		const canonical = canonicalWalletName(walletName);

		if (canonical === 'Continue with Google') {
			return <Google width={16} height={16} />;
		}
		if (canonical === 'Apple') {
			return <Apple sx={{ fontSize: 18 }} />;
		}

		const resolvedIcon =
			walletIcon ||
			(canonical === 'Petra' ? '/crypto/petra.png' : undefined);

		return (
			<Box
				sx={{
					width: 36,
					height: 36,
					borderRadius: '50%',
					backgroundColor: theme.palette.atlas.background.light,
					border: `1px solid ${theme.palette.atlas.overlay.slateAlpha40}`,
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					overflow: 'hidden',
				}}
			>
				{resolvedIcon ? (
					<img
						src={resolvedIcon}
						alt={canonical || walletName}
						style={{ width: 20, height: 20 }}
					/>
				) : (
					<Typography
						variant="caption"
						sx={{
							fontWeight: 600,
							color: theme.palette.atlas.text.lightStrong,
						}}
					>
						{(canonical || walletName).charAt(0)}
					</Typography>
				)}
			</Box>
		);
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					sx: {
						background: `linear-gradient(135deg, ${theme.palette.atlas.background.surface} 0%, ${theme.palette.atlas.background.appDeep} 100%)`,
						borderRadius: { xs: 3, sm: '28px' },
						border: `1px solid ${theme.palette.atlas.overlay.neutral}`,
						boxShadow: 'none',
						color: theme.palette.text.primary,
						px: { xs: 3, sm: 5 },
						py: { xs: 3, sm: 4 },
					},
				}}
				BackdropProps={{
					sx: {
						backgroundColor:
							theme.palette.atlas.overlay.slateAlpha35,
						backdropFilter: 'blur(4px)',
					},
				}}
			>
				<DialogTitle sx={{ px: 0, pt: 0, pb: { xs: 3, sm: 4 } }}>
					<Stack spacing={2.5}>
						<Stack
							direction="row"
							alignItems="flex-start"
							justifyContent="space-between"
						>
							<Box>
								<Typography
									color="textPrimary"
									sx={{
										fontFamily: 'AmigaForever',
										fontWeight: 600,
										fontSize: { xs: '18px', sm: '20px' },
										letterSpacing: 0.2,
									}}
								>
									Connect Your Wallet
								</Typography>
							</Box>
							<IconButton
								onClick={onClose}
								size="small"
								sx={{
									border: `1px solid ${theme.palette.atlas.overlay.neutral}`,
									backgroundColor:
										theme.palette.atlas.text.placeholder,
									color: theme.palette.atlas.text.dark,
									'&:hover': {
										backgroundColor:
											theme.palette.atlas.background
												.white,
									},
								}}
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Stack>
					</Stack>
				</DialogTitle>
				<DialogContent sx={{ px: 0, pb: 0 }}>
					<Stack spacing={3}>
						{walletOptions.map(
							({ adapterName, displayName, icon }) => {
								const canonical =
									canonicalWalletName(displayName);
								const isGoogle =
									canonical === 'Continue with Google';
								const isApple = canonical === 'Apple';
								const isPrimary = canonical === 'Petra';
								const buttonLabel = isGoogle
									? 'Google'
									: isApple
									? 'Apple'
									: displayName;

								return (
									<ConnectButton
										key={adapterName}
										walletKey={adapterName}
										walletName={buttonLabel}
										onSuccess={handleSuccess}
										onLoginStart={() =>
											setConnectingWallet(adapterName)
										}
										onLoginEnd={() =>
											setConnectingWallet(null)
										}
										disabled={
											connectingWallet !== null &&
											connectingWallet !== adapterName
										}
										startIcon={renderStartIcon(
											buttonLabel,
											icon,
										)}
										endIcon={
											<ArrowForwardIcon
												sx={{ fontSize: 18 }}
											/>
										}
										fullWidth
										sx={{
											fontFamily: 'AmigaForever',
											fontSize: '13px',
											fontWeight: 500,
											textTransform: 'none',
											backgroundColor: isGoogle
												? theme.palette.atlas.background
														.surface
												: isPrimary
												? theme.palette.atlas.background
														.surfaceStrong
												: theme.palette.atlas.background
														.surface,
											paddingBlock: '14px',
											color: isPrimary
												? theme.palette.text.primary
												: theme.palette.atlas.text
														.placeholder,
											borderRadius: 3,
											border: isPrimary
												? `1px solid ${theme.palette.atlas.border.strong}`
												: `1px solid ${theme.palette.atlas.overlay.slateAlpha12}`,
											justifyContent: 'space-between',
											boxShadow: 'none',
											transition:
												'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.2s ease',
											'&:hover': {
												backgroundColor:
													theme.palette.atlas
														.background
														.surfaceAccent,
												borderColor: isPrimary
													? theme.palette.atlas.border
															.accent
													: theme.palette.atlas
															.overlay
															.slateAlpha20,
												transform: 'translateY(-1px)',
											},
											'&:disabled': {
												backgroundColor:
													theme.palette.atlas
														.background.light,
												color: theme.palette.atlas.text
													.light,
												borderColor:
													theme.palette.atlas.overlay
														.slateAlpha40,
												opacity: 0.8,
											},
										}}
									/>
								);
							},
						)}
					</Stack>
				</DialogContent>
			</Dialog>
			<TermsAndConditionModal
				open={termsModalOpen}
				onClose={handleTermsCancel}
				onAccept={handleTermsAccept}
			/>
		</>
	);
}
